import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  createFilterOptions,
  styled,
} from "@mui/material";
import { debounce } from "lodash";
import { searchCustomers } from "../../../services/contractorService";

const FieldsetWrapper = styled("fieldset")(({ error }) => ({
  borderRadius: "12px",
  padding: "5px 12px",
  border: error ? "1px solid red" : "1px solid rgba(18, 18, 21, 0.10)",
  backgroundColor: "#fff",
  fontFamily: "Inter Tight",
  transition: "border-color 0.3s ease",
  "&:focus-within": {
    borderColor: "#4D6BF6",
  },
}));

const Legend = styled("legend")(({ error }) => ({
  fontSize: "13px",
  fontWeight: 500,
  padding: "0 5px",
  color: error ? "red" : "#0D0D0E",
  whiteSpace: "nowrap",
}));

const filter = createFilterOptions({
  stringify: (opt) => {
    const name = [opt.first_name, opt.last_name].filter(Boolean).join(" ");
    return name || opt.username || "";
  },
});

const formatCustomer = (raw) => {
  if (!raw) return null;
  const fullName =
    raw.username ||
    [raw.first_name, raw.middle_name, raw.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  // `client_id` on the customer object is what we send as `client_id` in
  // the proposal payload. For existing customers, that's their `user_id`
  // if they have one (registered user) — otherwise 0 (unregistered).
  const userId = raw.user_id;
  const clientIdFromUserId = userId == null ? 0 : userId;
  return {
    id: raw.id || raw.customer_id || 0,
    client_id:
      raw.client_id != null
        ? raw.client_id
        : clientIdFromUserId,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    username: fullName,
    customer_email: raw.email || raw.customer_email || "",
    phone_code: raw.phone_code || "",
    phone_no: raw.phone_no || "",
  };
};

const CustomerSelect = ({
  selected,
  onSelect,
  onRequestCreate,
  error = false,
  helperText = "",
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCustomers = useMemo(
    () =>
      debounce(async (q) => {
        setLoading(true);
        try {
          const res = await searchCustomers(q);
          const data = Array.isArray(res?.data) ? res.data : [];
          setOptions(data.map(formatCustomer).filter(Boolean));
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    fetchCustomers(inputValue.trim());
    return () => fetchCustomers.cancel?.();
  }, [inputValue, fetchCustomers]);

  return (
    <FormControl fullWidth error={error}>
      <FieldsetWrapper error={error}>
        <Legend error={error}>Customer</Legend>
        <Autocomplete
          fullWidth
          freeSolo
          disableClearable={false}
          disabled={disabled}
          value={selected || null}
          inputValue={inputValue}
          onInputChange={(_, v) => setInputValue(v)}
          options={options}
          loading={loading}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option?.username || ""
          }
          isOptionEqualToValue={(opt, val) =>
            (opt?.id ?? opt?.username) === (val?.id ?? val?.username)
          }
          filterOptions={(opts, params) => {
            const filtered = filter(opts, params);
            const trimmed = (params.inputValue || "").trim();
            const exists = opts.some(
              (o) =>
                (o.username || "").toLowerCase() === trimmed.toLowerCase()
            );
            if (trimmed && !exists) {
              filtered.push({
                __isCreate: true,
                inputValue: trimmed,
                username: `Add "${trimmed}"`,
              });
            }
            return filtered;
          }}
          onChange={(_, value) => {
            if (value && value.__isCreate) {
              // MUI updates the input to the option's label ('Add "Riya"')
              // when this option is picked. If the user then cancels the
              // create-customer modal we'd be left with that label in the
              // field — reset it back to the bare typed name so the input
              // reads "Riya" instead.
              setInputValue(value.inputValue || "");
              onRequestCreate?.(value.inputValue);
              return;
            }
            onSelect?.(value || null);
          }}
          renderOption={(props, option, { selected: isSelected }) => {
            const { key, style, ...rest } = props;
            const bg = option.__isCreate
              ? "#fff"
              : isSelected
              ? "#EEF2FF"
              : "#fff";
            return (
              <li
                {...rest}
                key={
                  option.__isCreate ? "__create" : option.id ?? option.username
                }
                style={{ ...(style || {}), backgroundColor: bg }}
              >
                {option.__isCreate ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#0D0D0E",
                      fontWeight: 500,
                    }}
                  >
                    {option.username}
                  </Typography>
                ) : (
                  <Box>
                    <Typography sx={{ fontSize: 14, color: "#0D0D0E" }}>
                      {option.username}
                    </Typography>
                    {option.customer_email && (
                      <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                        {option.customer_email}
                      </Typography>
                    )}
                  </Box>
                )}
              </li>
            );
          }}
          componentsProps={{
            paper: {
              sx: {
                backgroundColor: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                marginTop: "4px",
              },
            },
          }}
          ListboxProps={{
            sx: {
              backgroundColor: "#fff",
              padding: 0,
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Search or enter customer name…"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress size={14} sx={{ color: "#9CA3AF" }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                sx: {
                  fontSize: 14,
                  fontFamily: "Inter Tight",
                  padding: "2px 0 !important",
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "#fff" },
                  "&.Mui-focused": { backgroundColor: "#fff" },
                  "&:before, &:after": { display: "none" },
                },
              }}
            />
          )}
        />
      </FieldsetWrapper>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default CustomerSelect;
